import React, { useMemo } from "react";
import {
  FaBoxes,
  FaCashRegister,
  FaChartLine,
  FaMobileAlt,
  FaStore,
  FaUsers,
  FaWhatsapp,
} from "react-icons/fa";
import Header from "./Header";
import Footer from "./Footer";
import useScrollReveal from "./useScrollReveal";
import "./sitePages.css";
import "./samolCaisse.css";

const HIGHLIGHTS = [
  {
    icon: FaStore,
    label: "Simple a installer",
  },
  {
    icon: FaCashRegister,
    label: "Caisse rapide et stock",
  },
  {
    icon: FaUsers,
    label: "Equipe et multi-boutiques",
  },
  {
    icon: FaMobileAlt,
    label: "Mobile, tablette et web",
  },
];

const FEATURE_ITEMS = [
  {
    icon: FaCashRegister,
    title: "Encaissement rapide",
    text: "Ventes, stock et depenses dans une interface claire pour aller vite au comptoir.",
  },
  {
    icon: FaChartLine,
    title: "Pilotage simple",
    text: "Rapports, exports et suivi quotidien pour aider le gerant a piloter son activite.",
  },
  {
    icon: FaBoxes,
    title: "Croissance progressive",
    text: "Commencez en Free puis passez en Pro ou Business selon votre equipe et vos besoins.",
  },
];

const PRICING_PLANS = [
  {
    name: "Free",
    price: "0 FCFA",
    highlight: false,
    description: "Pour demarrer avec une boutique locale et tester la solution sans engagement.",
    features: ["1 boutique", "Caisse locale", "Stock local", "Depenses simples"],
  },
  {
    name: "Pro",
    price: "5 000 FCFA / an",
    highlight: true,
    description: "Pour les commerces qui veulent une meilleure organisation et un usage en equipe.",
    features: ["Multi-caissiers", "Rapports avances", "Export PDF / Excel", "Impression Bluetooth"],
  },
  {
    name: "Business",
    price: "10 000 FCFA / an",
    highlight: false,
    description: "Pour les structures avec plusieurs points de vente ou plusieurs appareils.",
    features: ["Multi-boutiques", "Synchronisation", "Sauvegarde cloud", "Support prioritaire"],
  },
];

function SamolCaissePage() {
  useScrollReveal();

  const whatsappUrl = "https://wa.me/2250171775165?text=Bonjour%20Samol%20Tech,%20je%20veux%20essayer%20Samol%20Caisse.";
  const logoUrl = `${process.env.PUBLIC_URL || ""}/samol-caisse-logo.png`;

  const trialUrl = useMemo(() => {
    if (process.env.REACT_APP_SAMOL_CAISSE_URL) {
      return process.env.REACT_APP_SAMOL_CAISSE_URL;
    }

    const { hostname, origin } = window.location;
    const isLocalHost = hostname === "localhost" || hostname === "127.0.0.1";

    if (isLocalHost) {
      return "http://127.0.0.1:3001";
    }

    return `${origin}/app/`;
  }, []);

  return (
    <div className="content-page samol-caisse-page">
      <Header />

      <main className="content-main samol-caisse-shell">
        <section className="samol-caisse-topbar reveal-on-scroll" data-reveal>
          <div className="samol-caisse-brand">
            <img src={logoUrl} alt="Samol Technologies" className="samol-caisse-brand-logo" />
            <div>
              <strong>Samol Caisse</strong>
              <span>Logiciel de caisse simple pour commerces</span>
            </div>
          </div>

          <div className="samol-caisse-topbar-actions">
            <a href="#samol-caisse-pricing" className="samol-caisse-link-btn">
              Tarifs
            </a>
            <a href={whatsappUrl} className="samol-caisse-btn samol-caisse-btn-secondary" target="_blank" rel="noreferrer">
              <FaWhatsapp aria-hidden="true" />
              WhatsApp
            </a>
            <a href={trialUrl} className="samol-caisse-btn samol-caisse-btn-primary" target="_blank" rel="noreferrer">
              Essayer maintenant
            </a>
          </div>
        </section>

        <section className="samol-caisse-hero">
          <div className="samol-caisse-hero-copy reveal-on-scroll" data-reveal>
            <span className="samol-caisse-kicker">Solution de caisse pour boutiques, maquis, patisseries et pharmacies</span>
            <h1>Samol Caisse aide les commerces a encaisser vite, suivre le stock et piloter l activite au quotidien.</h1>
            <p>
              Une solution simple, locale et evolutive pour travailler sur mobile, tablette ou web, avec une prise en
              main rapide.
            </p>

            <div className="samol-caisse-actions">
              <a href={trialUrl} className="samol-caisse-btn samol-caisse-btn-primary" target="_blank" rel="noreferrer">
                Essayer maintenant
              </a>
              <a href={whatsappUrl} className="samol-caisse-btn samol-caisse-btn-secondary" target="_blank" rel="noreferrer">
                <FaWhatsapp aria-hidden="true" />
                Parler sur WhatsApp
              </a>
              <a href="#samol-caisse-pricing" className="samol-caisse-text-link">
                Voir les tarifs
              </a>
            </div>
          </div>

          <div className="samol-caisse-hero-panel reveal-on-scroll" data-reveal>
            <div className="samol-caisse-panel-card">
              <span className="samol-caisse-panel-badge">Ce que vous obtenez</span>
              <h2>Une caisse claire pour vendre, suivre et faire grandir votre commerce.</h2>

              <div className="samol-caisse-feature-stack">
                {FEATURE_ITEMS.map((item) => {
                  const Icon = item.icon;

                  return (
                    <article className="samol-caisse-feature-row" key={item.title}>
                      <div className="samol-caisse-feature-row-icon">
                        <Icon aria-hidden="true" />
                      </div>
                      <div>
                        <h3>{item.title}</h3>
                        <p>{item.text}</p>
                      </div>
                    </article>
                  );
                })}
              </div>

              <div className="samol-caisse-panel-note">
                Essayez la version Free, puis activez Pro ou Business quand votre equipe et vos besoins grandissent.
              </div>
            </div>
          </div>
        </section>

        <section className="samol-caisse-highlight-strip reveal-on-scroll" data-reveal>
          {HIGHLIGHTS.map((item) => {
            const Icon = item.icon;

            return (
              <div className="samol-caisse-highlight-chip" key={item.label}>
                <Icon aria-hidden="true" />
                <span>{item.label}</span>
              </div>
            );
          })}
        </section>

        <section id="samol-caisse-pricing" className="samol-caisse-section reveal-on-scroll" data-reveal>
          <div className="samol-caisse-section-head">
            <span className="samol-caisse-eyebrow">Tarifs</span>
            <h2>Choisissez le plan adapte a la taille de votre commerce.</h2>
            <p>
              Le bouton WhatsApp permet de demander une demo, un accompagnement ou une activation selon votre besoin.
            </p>
          </div>

          <div className="samol-caisse-pricing-grid">
            {PRICING_PLANS.map((plan) => (
              <article
                className={`samol-caisse-pricing-card ${plan.highlight ? "is-highlighted" : ""}`}
                key={plan.name}
              >
                {plan.highlight ? <span className="samol-caisse-plan-tag">Plan recommande</span> : null}
                <h3>{plan.name}</h3>
                <strong className="pricing-plan-price">{plan.price}</strong>
                <p className="pricing-plan-description">{plan.description}</p>

                <ul className="samol-caisse-pricing-list">
                  {plan.features.map((feature) => (
                    <li key={feature}>{feature}</li>
                  ))}
                </ul>

                <div className="samol-caisse-pricing-actions">
                  <a href={trialUrl} className="samol-caisse-btn samol-caisse-btn-primary" target="_blank" rel="noreferrer">
                    Essayer maintenant
                  </a>
                  <a href={whatsappUrl} className="samol-caisse-btn samol-caisse-btn-secondary" target="_blank" rel="noreferrer">
                    <FaWhatsapp aria-hidden="true" />
                    WhatsApp
                  </a>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="samol-caisse-cta reveal-on-scroll" data-reveal>
          <div>
            <span className="samol-caisse-eyebrow">Pret a demarrer ?</span>
            <h2>Essayez Samol Caisse maintenant ou discutez sur WhatsApp pour une installation assistee.</h2>
            <p>La page est pensee pour convertir vite: presentation, tarifs clairs et acces direct au contact.</p>
          </div>

          <div className="samol-caisse-actions samol-caisse-actions-inline">
            <a href={trialUrl} className="samol-caisse-btn samol-caisse-btn-primary" target="_blank" rel="noreferrer">
              Essayer maintenant
            </a>
            <a href={whatsappUrl} className="samol-caisse-btn samol-caisse-btn-secondary" target="_blank" rel="noreferrer">
              <FaWhatsapp aria-hidden="true" />
              WhatsApp
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default SamolCaissePage;




